import { useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Select2 } from '@/components/forms/Select2';
import { AsyncButton } from '@/components/common/AsyncButton';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { terminalManagementApi, type TerminalDataSourceDto } from '@/api/services/terminalManagement';
import { useEnum } from '@/features/common/enums/useEnumOptions';

// Terminal connection types offered to tenant users: DataSourceTypeForSelect
// subset MT4_TERMINAL=8, MT5_TERMINAL=9, MatchTraderApi=12.
const TERMINAL_CONNECTION_TYPE_VALUES = [8, 9, 12];

interface Props {
  mode: 'add' | 'edit';
  row?: TerminalDataSourceDto;
  onClose: () => void;
  onSuccess: () => void;
}

export function TerminalRequestModal({ mode, row, onClose, onSuccess }: Props) {
  const toast = useToast();
  const isEdit = mode === 'edit';
  const [connectionType, setConnectionType] = useState('');
  const connectionTypeOptions = useEnum('DataSourceTypeForSelect').filter((o) =>
    TERMINAL_CONNECTION_TYPE_VALUES.includes(Number(o.value)),
  );
  const [name, setName] = useState(row?.name ?? '');
  const [server, setServer] = useState(row?.server ?? '');
  const [accountLogin, setAccountLogin] = useState(row?.accountLogin ?? '');
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(isEdit ? !!row?.enabled : true);

  const submit = async () => {
    try {
      if (isEdit) {
        await terminalManagementApi.requestEdit({
          dataSourceId: row!.id,
          name: String(name),
          server: String(server),
          accountLogin: String(accountLogin),
          password: String(password),
          enabled,
        });
      } else {
        await terminalManagementApi.requestNew({
          connectionType: connectionType === '' ? 0 : Number(connectionType),
          name: String(name),
          server: String(server),
          accountLogin: String(accountLogin),
          password: String(password),
          enabled,
        });
      }
      toast.success('Your request has been submitted.');
      onSuccess();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to submit the request.');
    }
  };

  return (
    <Modal
      show
      title={isEdit ? 'Request LP Update' : 'Add New LP'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-dark btn-sm" onClick={onClose}>Cancel</button>
          <AsyncButton className="btn btn-primary btn-sm" onClick={submit}>Submit Request</AsyncButton>
        </>
      }
    >
      {!isEdit && (
        <div className="mb-2">
          <label className="form-label">Connection Type</label>
          <Select2 searchable={false} allowClear={false} placeholder="Select…" value={connectionType} options={connectionTypeOptions} onChange={(v) => setConnectionType(v ?? '')} />
        </div>
      )}
      <div className="mb-2">
        <label className="form-label">Name</label>
        <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="mb-2">
        <label className="form-label">Server</label>
        <input className="form-control" value={server} onChange={(e) => setServer(e.target.value)} />
      </div>
      <div className="mb-2">
        <label className="form-label">Account Login</label>
        <input className="form-control" value={accountLogin} onChange={(e) => setAccountLogin(e.target.value)} />
      </div>
      <div className="mb-2">
        <label className="form-label">{isEdit ? 'Password (leave blank to keep)' : 'Password'}</label>
        <input type="password" className="form-control" value={password} onChange={(e) => setPassword(e.target.value)} />
      </div>
      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id="tm-enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <label className="form-check-label fs-14" htmlFor="tm-enabled">Enabled</label>
      </div>
    </Modal>
  );
}
